/**
 * Search Service - Abstracted search functionality
 * Calls backend API for search operations
 */

import { apiRequest } from '../config/api.js'
import { handleApiError } from './errorHandler.js'
import { fromRailsFormat } from '../models/Course.js'
import { getCompetencies } from './competencyService.js'

/**
 * Search courses with filters and pagination
 * @param {Object} searchParams - Search parameters
 * @param {string} searchParams.query - Text search query (searches name, description, provider, instructor)
 * @param {number} searchParams.competencyId - Filter by competency ID
 * @param {string} searchParams.providerType - Filter by provider type (School/Private Business/Other)
 * @param {string} searchParams.courseType - Filter by course format (online-on-demand/online-scheduled/in-person)
 * @param {string} searchParams.costType - Filter by cost type (free/paid)
 * @param {number} searchParams.minCredits - Minimum credits
 * @param {number} searchParams.maxCredits - Maximum credits
 * @param {number} searchParams.maxCost - Maximum cost (for paid courses)
 * @param {string} searchParams.location - Filter by location (partial match)
 * @param {number} searchParams.page - Page number (1-indexed)
 * @param {number} searchParams.pageSize - Number of results per page
 * @param {string} searchParams.sortBy - Sort field (name, credits, cost, createdAt)
 * @param {string} searchParams.sortOrder - Sort order (asc/desc)
 * @param {string} searchParams.statusFilter - Status filter: active, any, all_except_inactive, in_progress, or specific status (default: active for public search)
 * @returns {Promise<Object>} Search results with pagination info
 */
export async function searchCourses(searchParams = {}) {
  try {
    const {
      query = '',
      competencyId = '',
      providerType = '',
      courseType = '',
      costType = '',
      minCredits = null,
      maxCredits = null,
      maxCost = null,
      location = '', 
      page = 1,
      pageSize = 10,
      sortBy = 'createdAt',
      sortOrder = 'desc',
      statusFilter = 'active'
    } = searchParams

    // Build request body with only non-empty parameters
    const body = {}
    if (query.trim()) body.query = query.trim()
    if (competencyId) body.competencyId = competencyId
    if (providerType) body.providerType = providerType
    if (courseType) body.courseType = courseType
    if (costType) body.costType = costType
    if (minCredits !== null && minCredits !== '') body.minCredits = minCredits
    if (maxCredits !== null && maxCredits !== '') body.maxCredits = maxCredits
    if (maxCost !== null && maxCost !== '') body.maxCost = maxCost
    if (location.trim()) body.location = location.trim()
    if (statusFilter) body.statusFilter = statusFilter
    body.page = page
    body.pageSize = pageSize
    body.sortBy = sortBy
    body.sortOrder = sortOrder

    const data = await apiRequest('/courses/search', {
      method: 'POST',
      body: JSON.stringify(body)
    })

    // Convert Rails format to frontend format
    return {
      courses: data.courses.map(course => fromRailsFormat(course)),
      pagination: data.pagination
    }
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

/**
 * Get available filter options for search
 * @returns {Promise<Object>} Available filter options
 */
export async function getFilterOptions() {
  try {
    const [competenciesResult, coursesResult] = await Promise.all([
      getCompetencies(),
      apiRequest('/courses?per_page=1000&page=1', { method: 'GET' })
    ])

    const competencies = Array.isArray(competenciesResult) ? competenciesResult : []
    const courses = Array.isArray(coursesResult) ? coursesResult : (coursesResult.data || [])

    // Get provider types from the provider association
    const providerTypes = [...new Set(
      courses
        .map(c => c.provider?.provider_type)
        .filter(Boolean)
    )].sort()
    
    const courseTypes = [...new Set(courses.map(c => c.course_type).filter(Boolean))].sort()
    
    const credits = courses.map(c => c.credits).filter(c => c !== null && c !== undefined)
    const minCredits = credits.length > 0 ? Math.min(...credits) : 0
    const maxCredits = credits.length > 0 ? Math.max(...credits) : 0
    
    const costs = courses
      .filter(c => c.cost_type === 'paid' && c.cost !== null && c.cost !== undefined)
      .map(c => c.cost)
    const maxCost = costs.length > 0 ? Math.max(...costs) : 0
    
    return {
      competencies,
      providerTypes,
      courseTypes,
      creditsRange: { min: minCredits, max: maxCredits },
      maxCost
    }
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

